// src/components/ui/Input.jsx
import React from "react";
import { cn } from "../../utils/cn";
import Label from "./Label.jsx";
import HelperText from "./HelperText.jsx";

/**
 * AfriMed Input
 * - Uses global SOT classes + Label + HelperText atoms
 * - Checkbox & radio inputs kept intact
 */
const Input = React.forwardRef(
  (
    {
      className,
      type = "text",
      label,
      description,
      error,
      required = false,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).slice(2, 10)}`;
    const describedById =
      description || error ? `${inputId}-desc` : undefined;

    // Checkbox
    if (type === "checkbox") {
      return (
        <div className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            id={inputId}
            ref={ref}
            aria-invalid={!!error}
            className={cn(
              "h-4 w-4 rounded-afrimed-xs border-afrimed-field border-afrimed-outline bg-afrimed-surface-container-low text-afrimed-primary focus:ring-2 focus:ring-afrimed-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              className
            )}
            {...props}
          />
          {label && (
            <Label htmlFor={inputId} error={!!error}>
              {label}
              {required && (
                <span className="text-afrimed-error ml-1">*</span>
              )}
            </Label>
          )}
        </div>
      );
    }

    // Radio
    if (type === "radio") {
      return (
        <div className="inline-flex items-center gap-2">
          <input
            type="radio"
            id={inputId}
            ref={ref}
            aria-invalid={!!error}
            className={cn(
              "h-4 w-4 rounded-full border-afrimed-field border-afrimed-outline bg-afrimed-surface-container-low text-afrimed-primary focus:ring-2 focus:ring-afrimed-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              className
            )}
            {...props}
          />
          {label && (
            <Label htmlFor={inputId} error={!!error}>
              {label}
              {required && (
                <span className="text-afrimed-error ml-1">*</span>
              )}
            </Label>
          )}
        </div>
      );
    }

    // Standard input
    return (
      <div className="space-y-2">
        {label && (
          <Label htmlFor={inputId} error={!!error}>
            {label}
            {required && (
              <span className="text-afrimed-error ml-1">*</span>
            )}
          </Label>
        )}

        <input
          id={inputId}
          ref={ref}
          type={type}
          aria-invalid={!!error}
          aria-describedby={describedById}
          className={cn(
            "afrimed-input",
            error && "afrimed-input--error",
            className
          )}
          {...props}
        />

        {(description || error) && (
          <HelperText id={describedById} error={!!error}>
            {error || description}
          </HelperText>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";
export default Input;